import * as React from 'react';

import { type Time } from '@internationalized/date';
import { DateInput, DateSegment } from '@rematter/ui-web';
import {
  TimeField as AriaTimeField,
  type TimeValue,
} from 'react-aria-components';

type BufferedTimeFieldProps = {
  'aria-label': string;
  value: Time;
  onChange: (value: Time) => void;
  isReadOnly?: boolean;
};

//
// ⚛️ ⚛️ ⚛️ BUFFERED TIME FIELD ⚛️ ⚛️ ⚛️
//
export const BufferedTimeField = ({
  'aria-label': ariaLabel,
  value,
  onChange,
  isReadOnly,
}: BufferedTimeFieldProps) => {
  // Refs
  const editingRef = React.useRef(false);
  const committedRef = React.useRef<Time>(value);
  const defaultValueRef = React.useRef<Time>(value);
  const draftRef = React.useRef<Time>(value);
  const hasInteractedRef = React.useRef(false);

  // States
  const [instanceKey, setInstanceKey] = React.useState(0);

  // Callbacks
  const commit = React.useCallback(() => {
    if (!hasInteractedRef.current) return;
    if (draftRef.current.compare(committedRef.current) === 0) return;
    committedRef.current = draftRef.current;
    onChange(draftRef.current);
  }, [onChange]);

  const handleBlur = React.useCallback(
    (e: React.FocusEvent<Element>) => {
      if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
      editingRef.current = false;
      commit();
    },
    [commit],
  );

  // Effects
  React.useEffect(() => {
    if (value.compare(committedRef.current) === 0) return;
    committedRef.current = value;
    defaultValueRef.current = value;
    draftRef.current = value;
    hasInteractedRef.current = false;
    if (!editingRef.current) setInstanceKey((k) => k + 1);
  }, [value]);

  return (
    <AriaTimeField
      key={instanceKey}
      aria-label={ariaLabel}
      isReadOnly={isReadOnly}
      defaultValue={defaultValueRef.current}
      onChange={(next: TimeValue | null) => {
        if (!next) return;
        draftRef.current = next as Time;
      }}
      onFocus={() => {
        editingRef.current = true;
        hasInteractedRef.current = true;
      }}
      onBlur={handleBlur}
      granularity="minute"
      className="@BufferedTimeField"
    >
      <DateInput className="flex h-10 items-center rounded-md border border-neutral/20 bg-white px-2">
        {(segment) => <DateSegment segment={segment} />}
      </DateInput>
    </AriaTimeField>
  );
};
